import { useId, useState } from 'react'
import { EyeIcon, EyeOffIcon } from './auth-icons'

export interface AuthInputProps {
	id?: string
	label?: string
	type?: 'text' | 'email' | 'password'
	placeholder?: string
	value: string
	onChange: (value: string) => void
	required?: boolean
	minLength?: number
	hideLabel?: boolean
	icon?: React.ReactNode
	'aria-label'?: string
	autoComplete?: string
	error?: string
}

export function AuthInput({
	id,
	label,
	type = 'text',
	placeholder,
	value,
	onChange,
	required = false,
	minLength,
	hideLabel = false,
	icon,
	'aria-label': ariaLabel,
	autoComplete,
	error,
}: AuthInputProps) {
	const generatedId = useId()
	const inputId = id ?? generatedId
	const [showPassword, setShowPassword] = useState(false)
	const isPassword = type === 'password'
	const inputType = isPassword ? (showPassword ? 'text' : 'password') : type

	return (
		<div>
			{label
				? (
					<label
						htmlFor={inputId}
						className={`block text-base font-medium tracking-tight text-gray-700 mb-2.5 ${
							hideLabel ? 'sr-only' : ''
						}`}
					>
						{label}
					</label>
				)
				: null}
			<div className="relative">
				{icon
					? (
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
							{icon}
						</div>
					)
					: null}
				<input
					id={inputId}
					type={inputType}
					placeholder={placeholder}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					required={required}
					minLength={minLength}
					autoComplete={autoComplete}
					aria-label={!label ? ariaLabel : undefined}
					aria-invalid={!!error}
					className={`
						w-full px-3 py-2.5 rounded-lg border
						${
						error
							? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
							: 'border-gray-200 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20'
					}
						outline-none transition-all text-base
						placeholder:text-gray-400
						${icon ? 'pl-9' : ''}
						${isPassword ? 'pr-9' : ''}
					`}
				/>
				{isPassword
					? (
						<button
							type="button"
							onClick={() => setShowPassword(!showPassword)}
							aria-label={showPassword ? 'Hide password' : 'Show password'}
							aria-pressed={showPassword}
							className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
						>
							{showPassword ? <EyeOffIcon /> : <EyeIcon />}
						</button>
					)
					: null}
			</div>
			{error
				? <p className="mt-1.5 text-sm text-red-600">{error}</p>
				: null}
		</div>
	)
}
